/// <amd-module name="Wit/DeliveryPlans/Board/Services/DeliveryPlansBoardLayoutService" />

import { RollupAggregation, RollupCalculationSettings, RollupType } from "Analytics/Boards/RollupQueries/Contracts";
import { IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { stableSort } from "VSS/Platform/Util/Array";
import { WorkItemRollupProvider } from "Wit/AgileAnalytics/WorkItemRollupProvider";
import { FieldInfo } from "Wit/Clients/Work/Contracts/Work";
import { getObservableWorkItemSorter } from "Wit/ClientWorkItemService/IClientWorkItem";
import { FieldLayoutMap, WorkItemLayout } from "Wit/Common/Controls/Boards/Interfaces";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { TeamBacklogMap } from "Wit/DeliveryPlans/Common/TeamBacklogMap";
import { getWorkItemUrl } from "Wit/DeliveryPlans/Common/Utils/RoutingUtils";
import { IDeliveryPlansWorkItem, IIteration, IObservableDeliveryPlansWorkItem, ITeam } from "../../Common/Interfaces";
import { IDeliveryPlansTeamService } from "../../Common/Services/DeliveryPlansTeamService";
import { compareSpans, ISpan } from "../../Common/Utils/DateUtils";

export interface BoardLayout {
    teams: TeamLayout[];
    spacerRows: number[];
    highestRow: number;
}

export interface DeliveryPlansWorkItemLayout extends WorkItemLayout<IDeliveryPlansWorkItem, IObservableDeliveryPlansWorkItem> {
    startRow: number;
}

export interface WorkItemContainerLayout {
    /**
     * Count of work items by type for the containing layout structure (team, iteration).
     * Counts will NOT include work items in sub-structures, e.g. a team layout will contain only counts of work items not owned by an iteration.
     * This value is intended to be used to display work item counts by type when a team is collapsed.
     */
    workItemCounts: { [witType: string]: number };

    /**
     * Work items that are owned by the containing layout structure (team, iteration).
     * This will include work items in sub-structures, e.g. a team layout will contain all work items
     * without an iteration AND all work items within iterations it owns
     */
    workItems: DeliveryPlansWorkItemLayout[];
}

export interface TeamLayout extends WorkItemContainerLayout {
    team: ITeam;
    startRow: number;
    endRow: number;
    expanded: IObservableValue<boolean>;
    index: number;
    iterations: IterationLayout[];
    rollupProviders?: WorkItemRollupProvider[];
    partialItems: number[];
}

export interface IterationLayout extends WorkItemContainerLayout {
    iteration: IIteration;
}

export interface DeliveryPlansInputData {
    teams: ITeam[];
    items: IObservableDeliveryPlansWorkItem[];
    coreFields: FieldInfo[];
    additionalFields: FieldInfo[];
    visibleIds?: number[];
}

/**
 * DeliveryPlansLayoutService is responsible for determining which row in the CSS grid each work item and team belongs on.
 */
export interface IDeliveryPlansBoardLayoutService extends IVssService {
    /**
     * Add a list of work items to the layout
     */
    addItems(items: IObservableDeliveryPlansWorkItem[]): void;

    /**
     * Clear layout data. Used when settings change what teams to display.
     */
    clear(): void;

    /**
     * Gets the information the board uses to layout the board
     */
    getLayout(): IObservableValue<BoardLayout | undefined>;

    /**
     * Get an observable containing teams and iterations. If the array changes,
     * it will change to a new array.
     */
    getTeams(): IObservableValue<ITeam[]>;

    /**
     * Set data required to construct board layout
     */
    setInputData(data: DeliveryPlansInputData): void;

    /**
     * Updates the board layout with the supplied list of visible work items
     * @param visibleIds Ids that are visible given the current filter
     */
    updateLayout(visibleIds?: number[]): void;

    /**
     * Delete work item from the layout
     */
    deleteItem(workItemId: number): void;
}

export const firstMarkerRow = 1;
export const monthHeaderRow = 6;
const firstRow = 7;

class DeliveryPlansBoardLayoutService extends VssService implements IDeliveryPlansBoardLayoutService {
    private _observableLayout: IObservableValue<BoardLayout | undefined>;
    // using an ObservableValue instead of an ObservableArray lets us use object equality on the array to detect changes
    private _teams: IObservableValue<ITeam[]>;
    private _rollupProviders: TeamBacklogMap<WorkItemRollupProvider[]>;
    private _workItems: { [id: number]: boolean } = {};
    private _workItemList: IObservableDeliveryPlansWorkItem[] = [];
    private _itemLayouts: { [id: string]: DeliveryPlansWorkItemLayout } = {};
    private _coreFields: FieldInfo[];
    private _additionalFields: FieldInfo[];
    private _tagStyles: Map<string, string>;
    private _visibleIds: Set<number>;
    private _layoutPending = false;

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);
        this._observableLayout = new ObservableValue(undefined);
        this._teams = new ObservableValue([]);
        this._rollupProviders = new TeamBacklogMap<WorkItemRollupProvider[]>();
    }

    public clear(): void {
        this._observableLayout.value = undefined;
        this._teams.value = [];
        this._workItems = {};
        this._workItemList = [];
    }

    public setInputData(data: DeliveryPlansInputData): void {
        this._coreFields = data.coreFields || [];
        this._additionalFields = data.additionalFields || [];
        if (data.visibleIds) {
            this._visibleIds = new Set(data.visibleIds);
        }

        this.addTeams(data.teams);
        this.addItems(data.items);
    }

    public addTeams(teams: ITeam[]): void {
        let teamUpdates = false;
        for (const t of teams) {
            const idAndBacklog = t.id + t.backlog;
            const team = this._teams.value.find(team => idAndBacklog === team.id + team.backlog);
            if (!team) {
                this._teams.value.push(t);
                const rollupProviders = t.rollupWorkItemTypes?.map(
                    witType => new WorkItemRollupProvider(this.pageContext, getRollupMetricForType(witType), t.projectId)
                );
                this._rollupProviders.set(t.id, t.backlog, rollupProviders);

                teamUpdates = true;
            } else {
                // even if the team is a duplicate it might have new iterations
                // ... but we need to check for duplicates there as well
                const currentIterations: { [it: string]: boolean } = {};
                team.iterations.forEach(i => (currentIterations[i.path] = true));
                const newIterations = t.iterations.filter(i => !currentIterations[i.path]);
                if (newIterations.length > 0) {
                    team.iterations.push(...newIterations);
                    team.iterations.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
                    teamUpdates = true;
                }
            }
        }

        if (teamUpdates) {
            // trigger a re-render of observers
            this._teams.value = [...this._teams.value];
            this.updateLayout();
        }
    }

    public addItems(items: IObservableDeliveryPlansWorkItem[]): void {
        // putting these in an object means we don't have to worry about duplicates
        for (const i of items) {
            if (!this._workItems[i.value.id]) {
                this._workItemList.push(i);
                this._workItems[i.value.id] = true;
            }
        }

        this.updateLayout();
    }

    public deleteItem(workItemId: number): void {
        if (this._workItems[workItemId]) {
            const index = this._workItemList.findIndex(workItem => workItem?.value.id === workItemId);
            if (index >= 0) {
                delete this._workItemList[index];
            }
            delete this._workItems[workItemId];

            this.updateLayout();
        }
    }

    public getLayout(): IObservableValue<BoardLayout | undefined> {
        return this._observableLayout;
    }

    public getTeams(): IObservableValue<ITeam[]> {
        return this._teams;
    }

    public updateLayout(visibleIds?: number[]): void {
        if (visibleIds) {
            this._visibleIds = new Set(visibleIds);
        }

        if (!this._layoutPending) {
            // defer a moment to let batched updates finish
            this._layoutPending = true;
            setTimeout(() => {
                this._layoutPending = false;
                this._observableLayout.value = this._buildLayout();
            }, 0);
        }
    }

    private _buildLayout(): BoardLayout | undefined {
        // input data hasn't been set yet
        if (!this._coreFields || !this._additionalFields) {
            return undefined;
        }

        const occupiedCells: {
            [row: number]: ISpan[];
        } = {};

        const spacerRows: number[] = [];
        // the highest occupied row
        let highestRow = firstRow - 1;
        const teamService = this.pageContext.getService<IDeliveryPlansTeamService>("IDeliveryPlansTeamService");

        const coreFields = this._coreFields.reduce((coreFields, field) => {
            coreFields[field.referenceName] = field;
            return coreFields;
        }, {} as FieldLayoutMap);
        const additionalFields = [...this._additionalFields];

        const teamLayouts = this._teams.value.map((team, i) => {
            if (i !== 0) {
                // add a spacer row between teams
                spacerRows.push(highestRow + 1);
                highestRow++;
            }
            // reserve a row for the team's iteration header
            const iterationRow = getCardRow({ startDate: new Date(0), endDate: new Date(9999, 1, 1) }, highestRow + 1);
            const iterations = team.iterations.reduce((map, iteration) => {
                map[iteration.path] = { iteration, workItems: [], workItemCounts: {} };
                return map;
            }, {} as { [iterationPath: string]: IterationLayout | undefined });

            // team layout scoped work items and work item type counts
            const partialItems = [];
            const teamWorkItemLayouts: DeliveryPlansWorkItemLayout[] = [];
            const teamWorkItemCounts: { [witType: string]: number } = {};
            const teamWorkItems = this._workItemList.filter(workItem => teamService.teamOwnsWorkItem(team, workItem.value));
            stableSort(teamWorkItems, getObservableWorkItemSorter(team.orderByField));

            for (const observableItem of teamWorkItems) {
                const item = observableItem.value;
                const type = item.getFieldValue<string>(CoreFieldRefNames.WorkItemType, /* throwOnMissing */ true);
                if (team.workItemTypeColorAndIcons[type]) {
                    if (item.partial) {
                        partialItems.push(item.id);
                        continue;
                    }

                    if (!this._visibleIds?.has(item.id)) {
                        continue;
                    }

                    const colorAndIcon = team.workItemTypeColorAndIcons[type];
                    const iterationPath = item.getFieldValue<string>(CoreFieldRefNames.IterationPath, /* throwOnMissing */ true);
                    try {
                        const row = getCardRow(item, iterationRow);
                        const itLayout = iterations[iterationPath];

                        // re-use itemlayouts so react.memo can work properly
                        const url = getWorkItemUrl(this.pageContext, String(item.id), team.projectId);
                        const layoutUpdate = {
                            startRow: row,
                            url,
                            workItem: observableItem,
                            coreFields,
                            additionalFields,
                            tagStyles: this._tagStyles,
                            workItemTypeColorAndIcon: colorAndIcon
                        };

                        const itemLayoutKey = team.id + item.id;
                        const existingLayout = this._itemLayouts[itemLayoutKey];
                        const itemLayout = existingLayout ? Object.assign(existingLayout, layoutUpdate) : layoutUpdate;

                        this._itemLayouts[itemLayoutKey] = itemLayout;
                        teamWorkItemLayouts.push(itemLayout);
                        itLayout?.workItems.push(itemLayout);

                        const workItemCounts = itLayout?.workItemCounts || teamWorkItemCounts;
                        if (!workItemCounts[type]) {
                            workItemCounts[type] = 1;
                        } else {
                            workItemCounts[type]++;
                        }
                    } catch (e) {
                        // don't let an exception prevent us from placing the rest of the items
                        $DEBUG && console.log(e);
                    }

                    // ensure this item is included in rollup providers
                    const providers = this._rollupProviders.get(team.id, team.backlog);
                    if (providers) {
                        for (const provider of providers) {
                            if (!provider.hasWorkItem(item.id)) {
                                provider.checkAndUpdateWorkItemIds([item.id]);
                            }
                        }
                    }
                }
            }

            // persist expanded observables between renders
            const expanded =
                this._observableLayout.value?.teams.find(t => t.team.id === team.id && t.team.backlog === team.backlog)?.expanded ||
                new ObservableValue(team.isExpanded);
            return {
                team,
                startRow: iterationRow,
                endRow: highestRow + 1,
                expanded,
                index: i,
                iterations: Object.values(iterations),
                rollupProviders: this._rollupProviders.get(team.id, team.backlog),
                workItemCounts: teamWorkItemCounts,
                workItems: teamWorkItemLayouts,
                partialItems
            } as TeamLayout;
        });

        const boardLayout: BoardLayout = {
            teams: teamLayouts,
            spacerRows,
            highestRow
        };

        return boardLayout;

        /** Find the first row on or after the minimumRow where this card will fit */
        function getCardRow(c: ISpan, minimumRow: number) {
            for (let row = minimumRow; ; row++) {
                let insertAt: number;
                if (occupiedCells[row]) {
                    insertAt = occupiedCells[row].length;
                    // see if the cell for any of this card's sprints is occupied
                    for (let i = 0; i < occupiedCells[row].length; i++) {
                        const comp = compareSpans(c, occupiedCells[row][i]);
                        if (comp < 0) {
                            insertAt = i;
                            break;
                        }
                        // if we overlap with a span on this row, we're not going to fit
                        if (comp === 0) {
                            insertAt = -1;
                            break;
                        }
                    }
                } else {
                    // if we're initializing this row, obviously it's free
                    occupiedCells[row] = [];
                    insertAt = 0;
                }
                if (insertAt >= 0) {
                    // this card will fit in this row
                    occupiedCells[row].splice(insertAt, 0, {
                        startDate: c.startDate,
                        endDate: c.endDate
                    });
                    if (row > highestRow) {
                        highestRow = row;
                    }
                    return row;
                }
            }
        }
    }
}

function getRollupMetricForType(wit: string): RollupCalculationSettings {
    return {
        aggregation: RollupAggregation.Count,
        aggregationField: undefined,
        backlogFilter: undefined,
        type: RollupType.Progress,
        workItemTypeFilter: wit
    };
}

Services.add("IDeliveryPlansBoardLayoutService", { serviceFactory: DeliveryPlansBoardLayoutService });
